import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { BOARD_MAT_PALETTE, getBoardMatById } from "./boardMatPalette";
import { getBoardMatSurfaceLayers, getBoardMatSwatchTextureSx } from "./boardMatSurface";

const SWATCH_SIZE = 24;
const GRID_SWATCH_SIZE = 28;
const GRID_COLUMNS = 5;

function MatSwatch({ preset, size = SWATCH_SIZE, selected = false, compact = false, sx = {} }) {
    const { baseSx } = getBoardMatSurfaceLayers(preset);
    const textureSx = getBoardMatSwatchTextureSx(preset);

    return (
        <Box
            aria-hidden
            sx={{
                position: "relative",
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: "50%",
                overflow: "hidden",
                border: selected
                    ? compact
                        ? "2px solid #3BA666"
                        : "2px solid #3BA666"
                    : "1.5px solid rgba(0,0,0,0.14)",
                boxShadow: selected && !compact ? "0 0 0 1px #fff" : "none",
                outline: selected && compact ? "1px solid #fff" : "none",
                outlineOffset: selected && compact ? -1 : 0,
                pointerEvents: "none",
                boxSizing: "border-box",
                ...sx,
            }}
        >
            <Box sx={{ position: "absolute", inset: 0, borderRadius: "inherit", ...baseSx }} />
            <Box sx={textureSx} />
        </Box>
    );
}

MatSwatch.propTypes = {
    preset: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        hex: PropTypes.string.isRequired,
    }).isRequired,
    size: PropTypes.number,
    selected: PropTypes.bool,
    compact: PropTypes.bool,
    sx: PropTypes.object,
};

const stopToolbarDrag = (e) => {
    e.stopPropagation();
};

function MatSwatchGrid({ matId, onMatIdChange, layout = "grid" }) {
    const isRow = layout === "row";
    return (
        <Box
            role="listbox"
            aria-label="Board mat colors"
            sx={
                isRow
                    ? {
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.75,
                      }
                    : {
                          display: "grid",
                          gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${GRID_SWATCH_SIZE + 10}px)`,
                          justifyContent: "center",
                          gap: 0.75,
                          boxSizing: "border-box",
                      }
            }
        >
            {BOARD_MAT_PALETTE.map((preset) => {
                const selected = preset.id === matId;
                return (
                    <Box
                        key={preset.id}
                        component="button"
                        type="button"
                        role="option"
                        aria-selected={selected}
                        aria-label={preset.name}
                        onClick={() => onMatIdChange(preset.id)}
                        sx={{
                            p: 0.375,
                            m: 0,
                            minHeight: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "none",
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: selected ? "rgba(59, 166, 102, 0.12)" : "transparent",
                            "&:hover": {
                                bgcolor: selected
                                    ? "rgba(59, 166, 102, 0.18)"
                                    : "rgba(0, 0, 0, 0.04)",
                            },
                        }}
                    >
                        <MatSwatch preset={preset} size={GRID_SWATCH_SIZE} selected={selected} />
                    </Box>
                );
            })}
        </Box>
    );
}

MatSwatchGrid.propTypes = {
    matId: PropTypes.string.isRequired,
    onMatIdChange: PropTypes.func.isRequired,
    layout: PropTypes.oneOf(["grid", "row"]),
};

/**
 * Compact mat picker: toolbar Select, or inline panel grid/row for settings menus.
 */
export default function BoardMatColorSwitcher({
    matId,
    onMatIdChange,
    ariaLabel = "Board mat color",
    variant = "toolbar",
}) {
    if (variant === "panel" || variant === "row") {
        return (
            <Box sx={{ width: "100%" }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, fontWeight: 600 }}
                >
                    {ariaLabel}
                </Typography>
                <MatSwatchGrid
                    matId={matId}
                    onMatIdChange={onMatIdChange}
                    layout={variant === "row" ? "row" : "grid"}
                />
            </Box>
        );
    }

    const active = getBoardMatById(matId);

    const select = (
        <Select
            size="small"
            value={matId}
            onChange={(e) => onMatIdChange(e.target.value)}
            aria-label={ariaLabel}
            data-play-toolbar-no-drag
            IconComponent={ArrowDropDownIcon}
            renderValue={() => <MatSwatch preset={active} selected compact />}
            MenuProps={{
                disableScrollLock: true,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                PaperProps: {
                    sx: {
                        mt: 0.5,
                        borderRadius: 1.5,
                        boxShadow: 3,
                        overflow: "visible",
                    },
                },
                MenuListProps: {
                    sx: {
                        display: "grid",
                        gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${GRID_SWATCH_SIZE + 10}px)`,
                        justifyContent: "center",
                        gap: 0.75,
                        p: 1.25,
                        boxSizing: "border-box",
                    },
                },
            }}
            sx={{
                flexShrink: 0,
                width: 52,
                height: 32,
                overflow: "visible",
                bgcolor: "transparent",
                "& .MuiOutlinedInput-root": {
                    overflow: "visible",
                },
                "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                    py: 0.25,
                    pl: 1,
                    pr: "22px !important",
                },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-icon": {
                    fontSize: "1.125rem",
                    color: "text.secondary",
                    right: 2,
                },
            }}
        >
            {BOARD_MAT_PALETTE.map((preset) => {
                const selected = preset.id === matId;
                return (
                    <MenuItem
                        key={preset.id}
                        value={preset.id}
                        aria-label={preset.name}
                        selected={selected}
                        sx={{
                            p: 0.375,
                            minHeight: 0,
                            justifyContent: "center",
                            borderRadius: 1,
                            "&.Mui-selected": {
                                bgcolor: "rgba(59, 166, 102, 0.12)",
                                "&:hover": { bgcolor: "rgba(59, 166, 102, 0.18)" },
                            },
                        }}
                    >
                        <MatSwatch preset={preset} size={GRID_SWATCH_SIZE} selected={selected} />
                    </MenuItem>
                );
            })}
        </Select>
    );

    return (
        <span
            style={{ display: "inline-flex", flexShrink: 0, overflow: "visible", paddingLeft: 2 }}
            data-play-toolbar-no-drag
            onPointerDown={stopToolbarDrag}
            onClick={stopToolbarDrag}
        >
            {select}
        </span>
    );
}

BoardMatColorSwitcher.propTypes = {
    matId: PropTypes.string.isRequired,
    onMatIdChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    variant: PropTypes.oneOf(["toolbar", "panel", "row"]),
};
